/**
 * 管理后台 · 概览（状态看板，见方案 2B.3 A 类）。
 *
 * 这张看板上的每个数字都是**计数**或**部署实例自身的运行信息**：
 * 账号数、家庭数、打卡记录、磁盘/内存/进程、证书剩余天数、备份新鲜度。
 * 页面上**没有任何**学生昵称、目标名、打卡内容或积分明细 —— 见方案 2B.1。
 *
 * 命名约定（`checkin` 表的行数）：**唯一叫法 = 「打卡记录」**。
 * 总数只在顶部指标卡出现一次，「数据规模」里只列它的子集（名字自带归属），
 * 同一个数字绝不在同一页上用两个名字出现两次 —— 旧的同义叫法已废弃，
 * 不要再引入第二种叫法。
 *
 * 同一条纪律也适用于「学生」：`student` 表与 `auth_user`（`role = student`）由
 * `uq_student_user`（`student.user_id` 上的唯一索引，**没有** `deleted_at IS NULL`
 * 的部分条件）一对一绑定，且两个计数都按 `deleted_at IS NULL` 过滤 ——
 * 所以 `accounts.students` 与 `counts.students` **正常状态下必然相等**（线上都是 3）。
 * 因此只在顶部指标卡出现一次（账号计数那一组），
 * 「数据规模」里不再重复列一行「学生档案」。
 *
 * ⚠️ 但这不是硬性不变量：只有在 `student.deleted_at` 与 `auth_user.deleted_at`
 * 始终同步时两者才相等（索引本身并不约束这一点）。万一将来出现"只软删了一边"
 * 的脏数据，看板上就不再有两个数字可以互相印证 —— 那时请用一次性 SQL 去核对，
 * 而**不要**把「学生档案」重新加回这张看板（那等于把"同一个数、两个名字"再挖一遍）。
 */
import { Badge, Card, CardHeader, Skeleton } from '../../components/ui/Card';
import { AdminShell } from '../../components/layout/AdminShell';
import {
  KeyValueList,
  ManualCommands,
  StatCard,
  formatBytes,
  formatUptime,
} from '../../components/admin/AdminBits';
import { useAdminBackupStatus, useAdminOverview } from '../../lib/queries';
import { formatDateTimeFull } from '../../lib/utils';
import { toErrorMessage } from '../../lib/api/error';

export function AdminOverviewPage() {
  const overview = useAdminOverview();
  const backup = useAdminBackupStatus();

  const data = overview.data;

  return (
    <AdminShell
      title="系统状态看板"
      description="只看这个部署实例：账号计数、资源占用、证书与备份。看不到任何家庭内容。"
      action={
        <button
          type="button"
          onClick={() => {
            void overview.refetch();
            void backup.refetch();
          }}
          className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          刷新
        </button>
      }
    >
      {overview.isLoading && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      )}

      {overview.isError && (
        <Card>
          <p role="alert" className="text-sm text-rose-700">
            读取看板失败：{toErrorMessage(overview.error)}
          </p>
        </Card>
      )}

      {data && (
        <div className="space-y-5">
          {/* ---------- 账号与家庭计数 ---------- */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatCard label="家长" value={data.accounts.parents} />
            <StatCard label="学生" value={data.accounts.students} />
            <StatCard label="系统管理员" value={data.accounts.admins} tone="brand" />
            <StatCard label="已停用账号" value={data.accounts.disabled} tone={data.accounts.disabled > 0 ? 'warn' : 'neutral'} />
            <StatCard label="家庭数" value={data.families} hint="家庭自用：单实例单家庭" />
            <StatCard
              label="打卡记录"
              value={data.counts.checkins}
              hint={`近 7 天新增 ${data.counts.checkinsLast7Days}`}
            />
          </div>

          <Card>
            <CardHeader
              title="数据规模（只有计数）"
              description="管理员只允许看计数类统计；学生昵称、目标名、打卡备注、积分明细都不可见。"
            />
            <KeyValueList
              columns={3}
              items={[
                // 「学生」的总数只在上面的指标卡里出现一次（与 auth_user role=student
                // 由 uq_student_user 一对一，正常状态下两个数相同）；这里不再重复列「学生档案」。
                { label: '目标节点', value: data.counts.nodes },
                { label: '打卡任务（habit）', value: data.counts.habits },
                // 「打卡记录」的总数只在上面的指标卡里出现一次；这里只补它的子集，
                // 且把归属写进标签（「其中」离开上一条就没有指代了）。
                { label: '打卡记录（已完成）', value: data.counts.checkinsDone },
                { label: '数据库大小', value: data.database.sizeHuman ?? '-' },
              ]}
            />
          </Card>

          {/* ---------- 运行状态 ---------- */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="主机与进程" description="只读信息，来自容器内的 os/process" />
              <KeyValueList
                columns={1}
                items={[
                  { label: '主机名', value: data.runtime.hostname },
                  { label: '平台', value: `${data.runtime.platform} ${data.runtime.arch}` },
                  { label: '内核', value: data.runtime.kernel },
                  { label: 'Node 版本', value: data.runtime.nodeVersion },
                  { label: 'CPU 核数', value: data.runtime.cpuCount },
                  { label: '负载（1/5/15 分钟）', value: data.runtime.loadAverage.map((n) => n.toFixed(2)).join(' / ') },
                  { label: '主机运行时长', value: formatUptime(data.runtime.uptimeSeconds) },
                  { label: '进程运行时长', value: formatUptime(data.runtime.processUptimeSeconds) },
                  {
                    label: '内存',
                    value: `${formatBytes(data.runtime.memory.totalBytes - data.runtime.memory.freeBytes)} / ${formatBytes(data.runtime.memory.totalBytes)}（${data.runtime.memory.usedPercent}%）`,
                  },
                  { label: '进程 RSS', value: formatBytes(data.runtime.processMemory.rssBytes) },
                ]}
              />
            </Card>

            <div className="space-y-4">
              <Card>
                <CardHeader title="磁盘" />
                {data.disk.available ? (
                  <KeyValueList
                    columns={1}
                    items={[
                      { label: '挂载点', value: data.disk.path ?? '-' },
                      { label: '总量', value: formatBytes(data.disk.totalBytes) },
                      { label: '可用', value: formatBytes(data.disk.availableBytes) },
                    ]}
                  />
                ) : (
                  <p className="text-sm text-slate-500">{data.disk.reason}</p>
                )}
              </Card>

              <Card>
                <CardHeader title="HTTPS 证书" />
                {data.certificate.available ? (
                  <KeyValueList
                    columns={1}
                    items={[
                      {
                        label: '剩余天数',
                        value: (
                          <Badge
                            tone={
                              (data.certificate.daysRemaining ?? 0) > 21
                                ? 'success'
                                : (data.certificate.daysRemaining ?? 0) > 7
                                  ? 'warn'
                                  : 'danger'
                            }
                          >
                            {data.certificate.daysRemaining ?? '?'} 天
                          </Badge>
                        ),
                      },
                      { label: '到期时间', value: data.certificate.validTo ?? '-' },
                      { label: '文件', value: data.certificate.path ?? '-' },
                    ]}
                  />
                ) : (
                  <p className="text-sm text-slate-500">{data.certificate.reason}</p>
                )}
                <ManualCommands
                  commands={[
                    '# Caddy 的证书在 caddy_data 卷里，宿主机上看：',
                    'docker compose -f deploy/docker-compose.yml exec caddy ls -l /data/caddy/certificates',
                  ]}
                />
              </Card>

              <Card>
                <CardHeader title="容器状态" />
                <p className="text-sm text-slate-500">{data.containers.reason}</p>
                <ManualCommands commands={data.containers.manualCommands} />
              </Card>
            </div>
          </div>

          {/* ---------- 备份 ---------- */}
          <Card>
            <CardHeader
              title="备份状态（只读）"
              description="按方案 2B.3 的替代做法：只显示状态与命令，不在网页上提供「手动备份 / 恢复」按钮"
            />
            {backup.isLoading && <Skeleton className="h-16" />}
            {backup.data && (
              <div className="space-y-3">
                {backup.data.configured ? (
                  <KeyValueList
                    columns={2}
                    items={[
                      {
                        label: '最近成功备份',
                        /*
                          「最近成功备份」是**台账类**时间：备份新鲜度靠它判断，
                          没有年份的 '10-16 09:52' 跨年时会被读成今年 —— 必须走
                          `formatDateTimeFull`（'YYYY-MM-DD HH:mm' + 家庭时区）。
                          这里是 `KeyValueList` 的一个值（等价于单元格），
                          不是"嵌在句子里"的短标签，所以不适用省年份那一套。
                        */
                        value: backup.data.lastSuccessAt
                          ? formatDateTimeFull(backup.data.lastSuccessAt)
                          : '未知',
                      },
                      {
                        label: '已过去',
                        value: (
                          <Badge tone={backup.data.stale ? 'danger' : 'success'}>
                            {backup.data.ageHours ?? '?'} 小时
                            {backup.data.stale ? '（超过 24 小时，请检查）' : ''}
                          </Badge>
                        ),
                      },
                      { label: '备份大小', value: backup.data.sizeHuman ?? '标记文件里没有大小信息' },
                      { label: '标记文件', value: backup.data.markerFile ?? '-' },
                    ]}
                  />
                ) : (
                  <p className="text-sm text-slate-500">{backup.data.reason}</p>
                )}

                <KeyValueList
                  columns={2}
                  items={[
                    {
                      label: '备份节点可达性',
                      value: !backup.data.node.configured ? (
                        '未配置探测地址'
                      ) : backup.data.node.reachable ? (
                        <Badge tone="success">
                          可达（HTTP {backup.data.node.status}，{backup.data.node.latencyMs} ms）
                        </Badge>
                      ) : (
                        <Badge tone="danger">不可达：{backup.data.node.reason ?? '探测失败'}</Badge>
                      ),
                    },
                    { label: '新鲜度阈值', value: `${backup.data.stalenessThresholdHours} 小时` },
                  ]}
                />

                <ManualCommands commands={backup.data.manualCommands} title="备份与恢复演练（在服务器上执行）" />
              </div>
            )}
          </Card>
        </div>
      )}
    </AdminShell>
  );
}
